import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../utils/api'
import { getHourlyEditContext } from '../services/HourlyInvoiceEditService'
import FixedUpdateInvoice from './UpdateInvoice'
import HourlyUpdateInvoice from '../components/UpdateInvoice/HourlyUpdateInvoice'

/**
 * /update-invoice/:caseId — decides WHICH editor opens.
 *
 * `pages/UpdateInvoice.jsx` (the fixed editor) is unchanged and is rendered
 * here verbatim; it still reads the route param itself.
 *
 * THE EXISTING FIXED EDITOR IS THE DEFAULT, ALWAYS.
 * This probe sits on the critical path of a screen that already works, so
 * every failure mode — a throw, a non-2xx, a timeout, a malformed body — falls
 * back to the fixed editor rather than stranding the user. The hourly wizard is
 * only reached on a positive, well-formed answer.
 */

/* The probe must not out-wait the user's patience. Eight seconds is long
 * enough for a cold Render dyno and short enough that a hung endpoint does not
 * look like a broken button. */
const PROBE_TIMEOUT_MS = 8000

export default function UpdateInvoiceRouter() {
    const { caseId } = useParams()
    const navigate = useNavigate()

    /* Lock the page behind the editor.
     *
     * Case Manager keeps case-detail mounted underneath this route (App.js
     * passes a backgroundLocation), so the document body is still the scroll
     * container and the page scrolled away behind the dialog. Scale does not
     * have this problem because its UI-kit Dialog sets the same style, so the
     * two portals behaved differently on the same action.
     *
     * Restores whatever was there rather than clearing it, so a parent that
     * had already locked scrolling keeps its lock when this unmounts. */
    useEffect(() => {
        const previous = document.body.style.overflow
        document.body.style.overflow = "hidden"
        return () => {
            document.body.style.overflow = previous
        }
    }, [])


    const [resolving, setResolving] = useState(true)
    const [invoiceType, setInvoiceType] = useState('fixed')
    /* Handed to the hourly wizard so it does not refetch what was just read. */
    const [hourlyContext, setHourlyContext] = useState(null)

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const [detailsRes, contextRes] = await Promise.allSettled([
                    api.get(`/cases/${caseId}/hourly-billing/details`, {
                        timeout: PROBE_TIMEOUT_MS,
                    }),
                    getHourlyEditContext(caseId, { timeout: PROBE_TIMEOUT_MS }),
                ])
                if (cancelled) return

                const details =
                    detailsRes.status === 'fulfilled'
                        ? detailsRes.value?.data?.data || null
                        : null
                const context =
                    contextRes.status === 'fulfilled' ? contextRes.value || null : null

                /* A case counts as fixed-billed only when a payments row
                 * carries REAL money.
                 *
                 * An hourly case very often also has payments rows — $0
                 * placeholders written at booking so the party exists in the
                 * finance model before any hours are logged. Treating their
                 * mere presence as proof of a fixed invoice sends every one of
                 * those cases to the fixed editor and leaves the real hourly
                 * invoice unreachable. Note `defendentPayments` — that spelling
                 * is part of the CM BE contract. */
                const isRealPayment = (p) => !!p && Number(p.amount) > 0
                const hasFixedInvoice =
                    (details?.plaintiffPayments || []).some(isRealPayment) ||
                    (details?.defendentPayments || []).some(isRealPayment)

                if (hasFixedInvoice) {
                    setInvoiceType('fixed')
                    return
                }

                if (Array.isArray(context?.invoices) && context.invoices.length > 0) {
                    setHourlyContext(context)
                    setInvoiceType('hourly')
                    return
                }

                /* No hourly invoice — a zero-amount fixed row (a fully-couponed
                 * booking, or a party excused by the fee split), or nothing we
                 * could read at all. Either way the fixed editor is where this
                 * belongs; it renders its own "Failed to load case" state. */
                setInvoiceType('fixed')
            } catch (_err) {
                if (!cancelled) setInvoiceType('fixed')
            } finally {
                if (!cancelled) setResolving(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [caseId])

    /* The fixed editor's own loading shell, so the probe is invisible: the same
     * chrome stays on screen from click to editor and nothing flashes. */
    if (resolving) {
        return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xl">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-[1180px] max-h-[92vh] overflow-hidden relative">
                    <button
                        type="button"
                        onClick={() => navigate(`/cases/${caseId}`)}
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 z-50"
                        aria-label="Close"
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M5 5l10 10M15 5L5 15" />
                        </svg>
                    </button>
                    <div className="px-6 pt-5 pb-3">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Update Invoice
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Choose which invoice to update.
                        </p>
                        <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center gap-1.5">
                                {[1, 2, 3].map((n) => (
                                    <div
                                        key={n}
                                        className="h-2 w-8 rounded-full"
                                        style={
                                            n === 1
                                                ? { backgroundColor: '#115E59' }
                                                : { backgroundColor: '#E5E7EB' }
                                        }
                                    />
                                ))}
                            </div>
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                                Step 1 of 3
                            </span>
                        </div>
                    </div>
                    <div className="px-6 py-16 flex items-center justify-center">
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span className="inline-block w-4 h-4 rounded-full border-2 border-gray-200 border-t-[#115E59] animate-spin" />
                            Loading…
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (invoiceType === 'hourly') {
        return (
            <HourlyUpdateInvoice
                caseId={caseId}
                initialContext={hourlyContext}
                onClose={() =>
                    navigate(`/cases/${caseId}`, { state: { refresh: true } })
                }
            />
        )
    }

    return <FixedUpdateInvoice />
}
